/* eslint-disable react/prop-types */
/* eslint-disable react/react-in-jsx-scope */
import * as pdfjsLib from 'pdfjs-dist';
import { useRef, useEffect, useState, useCallback } from 'react';

pdfjsLib.GlobalWorkerOptions.workerSrc = '//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

// ── Inject text-layer CSS once ────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('_pdf_tl_css')) {
  const s = document.createElement('style');
  s.id = '_pdf_tl_css';
  s.textContent = `
    .pdf-tl { position:absolute; top:0; left:0; overflow:hidden; line-height:1; user-select:text; pointer-events:none; }
    .pdf-tl > span { color:transparent; position:absolute; white-space:pre; transform-origin:0 0; cursor:text; pointer-events:auto; }
    .pdf-tl mark { background:rgba(255,215,0,.55); color:transparent; border-radius:1px; }
    .pdf-tl mark.pdf-hi { background:rgba(255,120,0,.85); }
  `;
  document.head.appendChild(s);
}

// ── Highlight helpers ─────────────────────────────────────────────────────────
function applyHighlightsToLayer(layer, query, allMatches, activeIdx, pageNum) {
  if (!layer) return;
  layer.querySelectorAll('span').forEach((span) => {
    // eslint-disable-next-line no-self-assign
    if (span.querySelector('mark')) span.textContent = span.textContent;
  });
  if (!query.trim() || !allMatches.length) return;

  const pageGlobalIdxs = allMatches.reduce((acc, m, gi) => {
    if (m.pageNum === pageNum) acc.push(gi);
    return acc;
  }, []);

  const re = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
  let count = 0;
  layer.querySelectorAll('span').forEach((span) => {
    re.lastIndex = 0;
    const text = span.textContent;
    if (!text || !re.test(text)) return;
    re.lastIndex = 0;
    span.innerHTML = text.replace(re, (m) => {
      const gi = pageGlobalIdxs[count] ?? -1;
      const cls = gi === activeIdx ? ' class="pdf-hi"' : '';
      count++;
      return `<mark${cls}>${m}</mark>`;
    });
  });
}

// ── Toolbar styles ────────────────────────────────────────────────────────────
const tbBtn = (off = false) => ({
  padding: '4px 10px',
  border: '1px solid #555',
  borderRadius: 4,
  background: off ? '#2e2e2e' : '#4a4a4a',
  color: off ? '#666' : '#eee',
  cursor: off ? 'not-allowed' : 'pointer',
  fontSize: 13,
  lineHeight: '1.4',
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

const tbInput = (w) => ({
  padding: '4px 7px',
  border: '1px solid #555',
  borderRadius: 4,
  background: '#3a3a3a',
  color: '#eee',
  fontSize: 13,
  width: w,
  outline: 'none',
  textAlign: w <= 52 ? 'center' : 'left',
  flexShrink: 0,
});

// ── Component ─────────────────────────────────────────────────────────────────
export default function PDFViewer({ pdfLink }) {
  const scrollRef = useRef(null);
  const pdfRef = useRef(null);
  const pageWrapRefs = useRef({}); // { [pageNum]: wrapper div }
  const renderTasksRef = useRef({}); // { [pageNum]: renderTask }
  const scaleRef = useRef(window.innerWidth < 768 ? 0.8 : 1.2);

  // Mirror refs for use inside async callbacks (avoid stale closures)
  const qRef = useRef('');
  const mRef = useRef([]);
  const miRef = useRef(-1);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pages, setPages] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [curPage, setCurPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [scale, setScale] = useState(scaleRef.current);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [matchIdx, setMatchIdx] = useState(-1);
  const [searching, setSearching] = useState(false);
  const [renderingPage, setRenderingPage] = useState(null);

  // ── Render a single page ────────────────────────────────────────────────────
  const renderPage = useCallback(async (pageNum, pageScale) => {
    if (!pdfRef.current) return;
    const wrapper = pageWrapRefs.current[pageNum];
    if (!wrapper) return;

    renderTasksRef.current[pageNum]?.cancel();
    renderTasksRef.current[pageNum] = null;
    setRenderingPage(pageNum);

    try {
      const page = await pdfRef.current.getPage(pageNum);
      const vp = page.getViewport({ scale: pageScale });

      const canvas = wrapper.querySelector('canvas');
      const tl = wrapper.querySelector('.pdf-tl');
      if (!canvas || !tl) return;

      canvas.width = vp.width;
      canvas.height = vp.height;

      const task = page.render({ canvasContext: canvas.getContext('2d'), viewport: vp });
      renderTasksRef.current[pageNum] = task;
      await task.promise;

      // Text layer
      tl.innerHTML = '';
      tl.style.width = `${vp.width}px`;
      tl.style.height = `${vp.height}px`;

      const tc = await page.getTextContent();
      const tlTask = pdfjsLib.renderTextLayer({
        textContentSource: tc,
        container: tl,
        viewport: vp,
        textDivs: [],
      });
      await (tlTask?.promise ?? tlTask);

      applyHighlightsToLayer(tl, qRef.current, mRef.current, miRef.current, pageNum);
    } catch (e) {
      if (e?.name !== 'RenderingCancelledException')
        console.error('PDF render error p.' + pageNum, e);
    } finally {
      setRenderingPage((p) => (p === pageNum ? null : p));
    }
  }, []);

  // ── Load PDF document ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!pdfLink) return;

    Object.values(renderTasksRef.current).forEach((t) => t?.cancel());
    renderTasksRef.current = {};

    setLoading(true);
    setError(null);
    setCurPage(1);
    setPageInput('1');
    setPages([]);
    setTotalPages(0);
    setMatches([]);
    mRef.current = [];
    setMatchIdx(-1);
    miRef.current = -1;
    qRef.current = '';
    setQuery('');

    pdfjsLib
      .getDocument(pdfLink)
      .promise.then((pdf) => {
        pdfRef.current = pdf;
        const n = pdf.numPages;
        setTotalPages(n);
        setPages(Array.from({ length: n }, (_, i) => i + 1));
      })
      .catch(() => setError('Failed to load PDF. Please try again.'))
      .finally(() => setLoading(false));

    return () => Object.values(renderTasksRef.current).forEach((t) => t?.cancel());
  }, [pdfLink]);

  // ── Keep scaleRef in sync ───────────────────────────────────────────────────
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  // ── Lazy-render pages via IntersectionObserver ──────────────────────────────
  // Re-runs on scale change: cancels old renders, observer fires for visible pages
  useEffect(() => {
    if (!pages.length || !scrollRef.current) return;

    // Cancel all in-flight renders so they re-render at new scale
    Object.values(renderTasksRef.current).forEach((t) => t?.cancel());
    renderTasksRef.current = {};

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            renderPage(+entry.target.dataset.page, scaleRef.current);
          }
        });
      },
      { root: scrollRef.current, rootMargin: '400px 0px' },
    );

    Object.values(pageWrapRefs.current).forEach((el) => {
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [pages, scale, renderPage]);

  // ── Track current visible page ──────────────────────────────────────────────
  useEffect(() => {
    if (!pages.length || !scrollRef.current) return;

    const obs = new IntersectionObserver(
      (entries) => {
        let best = { ratio: 0, page: 1 };
        entries.forEach((e) => {
          if (e.intersectionRatio > best.ratio)
            best = { ratio: e.intersectionRatio, page: +e.target.dataset.page };
        });
        if (best.ratio > 0) {
          setCurPage(best.page);
          setPageInput(String(best.page));
        }
      },
      { root: scrollRef.current, threshold: [0, 0.25, 0.5, 0.75, 1] },
    );

    Object.values(pageWrapRefs.current).forEach((el) => {
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [pages]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const scrollToPage = (pageNum) => {
    const el = pageWrapRefs.current[pageNum];
    if (el && scrollRef.current) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const refreshAllHighlights = (q, allMatches, activeIdx) => {
    Object.entries(pageWrapRefs.current).forEach(([numStr, wrapper]) => {
      const tl = wrapper?.querySelector('.pdf-tl');
      if (tl) applyHighlightsToLayer(tl, q, allMatches, activeIdx, +numStr);
    });
  };

  // ── Search ──────────────────────────────────────────────────────────────────
  const doSearch = async () => {
    const q = query.trim();
    if (!pdfRef.current || !q) {
      setMatches([]);
      mRef.current = [];
      setMatchIdx(-1);
      miRef.current = -1;
      qRef.current = '';
      refreshAllHighlights('', [], -1);
      return;
    }

    setSearching(true);
    const found = [];
    const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');

    for (let p = 1; p <= pdfRef.current.numPages; p++) {
      const tc = await (await pdfRef.current.getPage(p)).getTextContent();
      const text = tc.items.map((i) => i.str).join('');
      re.lastIndex = 0;
      let m;
      while ((m = re.exec(text)) !== null) found.push({ pageNum: p, offset: m.index });
    }

    setSearching(false);
    setMatches(found);
    mRef.current = found;
    qRef.current = q;

    if (found.length) {
      setMatchIdx(0);
      miRef.current = 0;
      scrollToPage(found[0].pageNum);
      refreshAllHighlights(q, found, 0);
    } else {
      setMatchIdx(-1);
      miRef.current = -1;
    }
  };

  const stepMatch = (dir) => {
    if (!matches.length) return;
    const next = (matchIdx + dir + matches.length) % matches.length;
    setMatchIdx(next);
    miRef.current = next;
    scrollToPage(matches[next].pageNum);
    refreshAllHighlights(qRef.current, mRef.current, next);
  };

  const goPage = (val) => {
    const p = Math.max(1, Math.min(Number(val), totalPages));
    if (!isNaN(p) && p >= 1) {
      scrollToPage(p);
      setCurPage(p);
      setPageInput(String(p));
    } else setPageInput(String(curPage));
  };

  const zoom = (d) => setScale((s) => +Math.max(0.4, Math.min(3, s + d)).toFixed(1));

  // ── JSX ─────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {/* ── Toolbar ── */}
      {/* <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 5,
          padding: '6px 10px',
          background: '#383838',
          borderBottom: '1px solid #1a1a1a',
          flexShrink: 0,
        }}
      > */}
      {/* <input
          type="text"
          placeholder="Search…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            qRef.current = e.target.value;
          }}
          onKeyDown={(e) => e.key === 'Enter' && doSearch()}
          style={tbInput(130)}
        />
        <button
          onClick={doSearch}
          disabled={searching || loading}
          style={tbBtn(searching || loading)}
        >
          {searching ? '…' : '🔍'}
        </button> */}

      {/* {matches.length > 0 && (
          <>
            <button onClick={() => stepMatch(-1)} style={tbBtn()} title="Prev match">
              ◀
            </button>
            <span
              style={{
                fontSize: 12,
                color: '#bbb',
                minWidth: 48,
                textAlign: 'center',
                flexShrink: 0,
              }}
            >
              {matchIdx + 1}/{matches.length}
            </span>
            <button onClick={() => stepMatch(1)} style={tbBtn()} title="Next match">
              ▶
            </button>
          </>
        )}
        {query && !searching && matches.length === 0 && (
          <span style={{ fontSize: 12, color: '#f88', flexShrink: 0 }}>No results</span>
        )} */}

      {/* <div style={{ flex: 1, minWidth: 8 }} /> */}

      {/* Page nav */}
      {/* <button
          onClick={() => goPage(curPage - 1)}
          disabled={curPage <= 1}
          style={tbBtn(curPage <= 1)}
        >
          ‹
        </button>
        <input
          type="number"
          value={pageInput}
          min={1}
          max={totalPages}
          onChange={(e) => setPageInput(e.target.value)}
          onBlur={() => goPage(pageInput)}
          onKeyDown={(e) => e.key === 'Enter' && goPage(pageInput)}
          style={tbInput(44)}
        />
        <span style={{ fontSize: 13, color: '#999', flexShrink: 0 }}>/ {totalPages || '–'}</span>
        <button
          onClick={() => goPage(curPage + 1)}
          disabled={curPage >= totalPages}
          style={tbBtn(curPage >= totalPages)}
        >
          ›
        </button>

        <div
          style={{
            width: 1,
            background: '#555',
            alignSelf: 'stretch',
            margin: '2px 4px',
            flexShrink: 0,
          }}
        /> */}

      {/* Zoom */}
      {/* <button onClick={() => zoom(-0.2)} disabled={scale <= 0.4} style={tbBtn(scale <= 0.4)}>
          −
        </button>
        <span
          style={{ fontSize: 12, color: '#bbb', minWidth: 38, textAlign: 'center', flexShrink: 0 }}
        >
          {Math.round(scale * 100)}%
        </span>
        <button onClick={() => zoom(0.2)} disabled={scale >= 3} style={tbBtn(scale >= 3)}>
          +
        </button> */}

      {/* {renderingPage && (
          <span style={{ fontSize: 11, color: '#888', marginLeft: 4, flexShrink: 0 }}>
            p.{renderingPage}…
          </span>
        )} */}
      {/* </div> */}

      {/* ── Scrollable page stack ── */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'auto',
          background: '#525659',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px 12px',
          gap: 12,
        }}
      >
        {loading && <p style={{ color: '#ddd', margin: 'auto', fontSize: 14 }}>Loading PDF…</p>}
        {error && <p style={{ color: '#f88', margin: 'auto', fontSize: 14 }}>{error}</p>}

        {pages.map((pageNum) => (
          <div
            key={pageNum}
            data-page={pageNum}
            ref={(el) => {
              pageWrapRefs.current[pageNum] = el;
            }}
            style={{
              position: 'relative',
              background: '#fff',
              boxShadow: '0 2px 16px rgba(0,0,0,.55)',
              flexShrink: 0,
              lineHeight: 0,
            }}
          >
            {/* Page number label above each page */}
            {/* <div
              style={{
                position: 'absolute',
                top: -20,
                left: 0,
                fontSize: 11,
                color: curPage === pageNum ? '#4a9eff' : '#999',
                userSelect: 'none',
                transition: 'color .2s',
                lineHeight: 1,
              }}
            >
              Page {pageNum}
            </div> */}
            <canvas style={{ display: 'block', maxWidth: '100%' }} />
            <div className="pdf-tl" />
          </div>
        ))}
      </div>

      {/* ── Footer: dot pagination ── */}
      {totalPages > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 6,
            padding: '5px 12px',
            background: '#383838',
            borderTop: '1px solid #1a1a1a',
            flexShrink: 0,
            flexWrap: 'wrap',
          }}
        >
          {Array.from({ length: Math.min(totalPages, 12) }, (_, i) => {
            const p = totalPages <= 12 ? i + 1 : Math.round((i / 11) * (totalPages - 1)) + 1;
            const active = p === curPage;
            return (
              <div
                key={i}
                onClick={() => scrollToPage(p)}
                title={`Page ${p}`}
                style={{
                  width: active ? 9 : 6,
                  height: active ? 9 : 6,
                  borderRadius: '50%',
                  background: active ? '#4a9eff' : '#5a5a5a',
                  cursor: 'pointer',
                  transition: 'all .2s',
                  flexShrink: 0,
                }}
              />
            );
          })}
          <span style={{ fontSize: 11, color: '#777', marginLeft: 4 }}>
            {curPage} / {totalPages}
          </span>
        </div>
      )}
    </div>
  );
}
